import type {EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {AudioItem} from '../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type';
import {readCompanyVoiceClip, type CompanyVoiceClip, type VoiceReplacementPolicy} from '../../protocol/voice-replacement';
import {hasPendingTimelineEdit} from './edit-ownership';
import {insertTimelineTime} from './timeline-insert';
import {removeTimelineTime} from './timeline-ripple';
import type {EditorStarterAsset} from '../../vendor/src/features/remotion-editor/editor/assets/assets';
import type {CompanySpeechSettings} from '../../protocol/speech-clip';
import {MIN_PLAYBACK_RATE,MAX_PLAYBACK_RATE} from '../../vendor/src/features/remotion-editor/editor/inspector/controls/playback-rate-apply';
import {groupVoiceNaturalError} from './group-voice-natural';

export function retainVoiceReplacement(state: EditorState, asset: EditorStarterAsset, source: CompanyVoiceClip,
  settings: CompanySpeechSettings, attachmentId: string): {state: EditorState; assetId: string; alreadyApplied: boolean} {
  if (asset.type !== 'audio' || !attachmentId || attachmentId.length > 128) throw new Error('새 음성 자료를 확인해 주세요.');
  if (hasPendingTimelineEdit(state)) throw new Error('현재 편집을 마친 뒤 생성 음성을 다시 연결해 주세요.');
  const existing = Object.values(state.undoableState.assets).find(value => 'wakaSpeechId' in value && value.wakaSpeechId === attachmentId);
  if (existing) {
    if (existing.type !== 'audio') throw new Error('이미 연결된 결과의 종류를 확인해 주세요.');
    const target = state.undoableState.items[source.clipId];
    return {state, assetId: existing.id, alreadyApplied: target?.type === 'audio' && target.assetId === existing.id};
  }
  if (state.undoableState.assets[asset.id]) throw new Error('같은 이름의 원본을 덮어쓰지 않도록 연결을 멈췄습니다.');
  const linked = {...asset, wakaSpeechId: attachmentId,
    wakaSpeech: {...settings, sourceItemId: source.clipId, sourceKind: 'audio', text: source.text, sourceUrl: asset.remoteUrl}};
  return {assetId: linked.id, alreadyApplied: false, state: {...state,
    undoableState: {...state.undoableState, assets: {...state.undoableState.assets, [linked.id]: linked}},
    assetStatus: {...state.assetStatus, [linked.id]: {type: 'uploaded'}}}};
}

export type VoiceReplacementPlan =
  | {ok: true; state: EditorState; durationInFrames: number; playbackRate: number; shiftedClips: number; groupNaturalClipId?: string}
  | {ok: false; state: EditorState; reason: string};

/** One explicit edit. Keep every original asset, and never enlist the main-track link switch. */
export function planVoiceReplacement(state: EditorState, source: CompanyVoiceClip, assetId: string, policy: VoiceReplacementPolicy): VoiceReplacementPlan {
  const fail = (reason: string): VoiceReplacementPlan => ({ok: false, state, reason});
  if (hasPendingTimelineEdit(state)) return fail('진행 중인 편집을 먼저 마쳐 주세요. 새 음성은 자료함에 있습니다.');
  if (!['natural', 'fit', 'ripple-track'].includes(policy)) return fail('길이 처리 방법을 골라 주세요.');
  let current: CompanyVoiceClip;
  try {
    current = readCompanyVoiceClip(state.undoableState, source.clipId);
    if (current.signature !== source.signature) return fail('선택한 원본 음성이 바뀌었습니다. 새 음성은 자료함에서 직접 배치할 수 있습니다.');
  } catch (reason) { return fail(reason instanceof Error ? reason.message : '원본 음성을 확인해 주세요.'); }
  const groupPath = current.groupPath;
  if (groupPath?.length && policy === 'ripple-track') return fail('그룹 내부 음성은 다른 멤버를 이동하지 않습니다. 구간 맞추기 또는 병합 해제 후 교체를 사용해 주세요.');
  const before = state.undoableState;
  const original = before.items[source.clipId], asset = before.assets[assetId];
  const track = before.tracks.find(t => t.id === source.trackId);
  if (!original || original.type !== 'audio' || !asset || asset.type !== 'audio' || !track || track.locked
    || !Number.isFinite(asset.durationInSeconds) || asset.durationInSeconds <= 0 || asset.id === original.assetId) return fail('새 음성과 원본을 확인해 주세요.');
  const naturalFrames = Math.max(1, Math.ceil(asset.durationInSeconds * before.fps - 1e-7));
  const length = policy === 'fit' ? original.durationInFrames : naturalFrames;
  const rate = policy === 'fit' ? asset.durationInSeconds * before.fps / length : 1;
  if (!Number.isSafeInteger(length) || !Number.isSafeInteger(original.from + length)) return fail('새 음성의 길이를 확인해 주세요.');
  if (!Number.isFinite(rate) || rate < MIN_PLAYBACK_RATE || rate > MAX_PLAYBACK_RATE) return fail(`구간에 맞추면 재생속도가 ${MIN_PLAYBACK_RATE}~${MAX_PLAYBACK_RATE}배 범위를 벗어납니다. 다른 길이 처리 방법을 선택해 주세요.`);
  const oldEnd = original.from + original.durationInFrames, newEnd = original.from + length;
  const delta = length - original.durationInFrames;
  if(groupPath?.length&&policy==='natural'){
    const reason=groupVoiceNaturalError(state,original.id,length);if(reason)return fail(reason);
  }
  // Group natural replacement already checked the owning tree's audible items. The
  // containing root is not a peer of its own member.
  const peers = groupPath?.length && policy === 'natural' ? [] : track.items.filter(id => id !== original.id).map(id => before.items[id]);
  if (peers.some(item => !item)) return fail('오디오 트랙의 항목 연결을 확인해 주세요.');
  const audioOnlyTree = (id: string, seen = new Set<string>()): boolean => {
    if (seen.has(id)) return false;
    const item = before.items[id];
    return !!item && (item.type === 'audio' || (item.type === 'group' && item.children.length > 0
      && item.children.every(child => audioOnlyTree(child, new Set(seen).add(id)))));
  };
  if (policy === 'ripple-track' && delta && peers.some(item => item && item.from >= oldEnd && !audioOnlyTree(item.id))) {
    return fail('같은 트랙에 영상이나 다른 종류의 클립이 있습니다. 오디오 트랙을 나누거나 구간 맞추기를 선택해 주세요.');
  }
  if (delta && Object.values(before.transitions).some(t => t.sourceItemId === original.id || t.targetItemId === original.id)) {
    return fail('전환이 연결된 음성은 구간 맞추기를 사용하거나 전환을 조절한 뒤 교체해 주세요.');
  }
  if (delta && peers.some(item => item && item.from < oldEnd && item.from + item.durationInFrames > original.from)) {
    return fail('원래 구간에 다른 클립이 겹칩니다. 구간 맞추기를 사용하거나 트랙을 나눠 주세요.');
  }
  if (policy === 'natural' && peers.some(item => item && item.from < newEnd && item.from + item.durationInFrames > original.from)) {
    return fail('새 음성이 뒤 클립과 겹칩니다. 구간 맞추기 또는 같은 트랙 뒤 오디오 이동을 선택해 주세요.');
  }
  const replacement: AudioItem & {speechSource?: unknown} = {
    ...original, assetId, durationInFrames: length, audioStartFromInSeconds: 0, playbackRate: rate,
    audioFadeWindow:undefined,
    audioFadeInDurationInSeconds: Math.min(original.audioFadeInDurationInSeconds, length / before.fps),
    audioFadeOutDurationInSeconds: Math.min(original.audioFadeOutDurationInSeconds, length / before.fps),
  };
  // New sound must not masquerade as the old recording's synthesis facts.
  delete replacement.speechSource;
  let next: EditorState = {...state, undoableState: {...before, items: {...before.items, [original.id]: replacement}}};
  let shiftedClips = 0;
  if (policy === 'ripple-track' && delta) {
    shiftedClips = peers.filter(item => item && item.from >= oldEnd).length;
    if (delta > 0) {
      const opened = insertTimelineTime(next, [track.id], oldEnd, delta, [original.id]);
      if (!opened.ok) return fail('같은 트랙의 뒤 오디오를 이동하지 못했습니다. 다른 방법을 선택해 주세요.');
      next = opened.state;
    } else {
      const closed = removeTimelineTime(next, [track.id], [{start: newEnd, end: oldEnd}]);
      if (!closed.ok) return fail('같은 트랙의 빈 구간을 줄이지 못했습니다. 다른 방법을 선택해 주세요.');
      next = closed.state;
    }
  }
  return {ok: true, state: {...next, selectedItems: [original.id], ...(groupPath?.length ? {activeGroupId: groupPath[0]!, inspectedGroupMemberId: null} : {}), timelineTimeEditApplied: true}, durationInFrames: length, playbackRate: rate, shiftedClips,
    ...(groupPath?.length&&policy==='natural'?{groupNaturalClipId:original.id}:{})};
}
